/**
 * features/finance/ChartOfAccounts.tsx
 * ------------------------------------------------------------------
 * The school's account codes — Phase 2 of FINANCE-DESIGN.md.
 *
 * Rendered as a TREE (header -> children) rather than a flat list, because a
 * chart's meaning is in its grouping: "5000 EXPENDITURE" is not an account you
 * post to, it is the subtotal that tells you what the school spends.
 *
 * Two things this screen refuses to do, both deliberate:
 *
 *  - It never DELETES an account. Deleting one orphans every journal line
 *    that referenced it, and the ledger is append-only. Deactivating hides it
 *    from future pickers while leaving history readable.
 *  - It never lets you edit a CODE in place. The doc id is the code, so an
 *    in-place rename would silently re-point history. Create the new code and
 *    deactivate the old one — which is also what an auditor expects to see.
 *
 * The `systemKey` badge matters more than it looks: it is the handle the
 * posting engine resolves by, so an account carrying one cannot be
 * deactivated without breaking posting. That is surfaced, not just prevented.
 * ------------------------------------------------------------------ */
import { useMemo, useState } from 'react';
import { Plus, Pencil, EyeOff, Eye, AlertTriangle, Check, ListTree } from 'lucide-react';
import { Button, Spinner } from '@/components/ui/primitives';
import { FormModal, modalPrimaryBtn, modalGhostBtn } from '@/components/ui/glass';
import { useAuth } from '@/contexts/AuthContext';
import { getPermissions } from '@/lib/permissions';
import { useGlData } from '@/features/finance/useGlData';
import { saveAccount, setAccountActive } from '@/features/finance/glWrite';
import type { GlAccount, AccountType } from '@/lib/gl';
import { PageHeader, SoftCard, glassFieldClass, ExportBar } from '@/features/finance/ui';
import { downloadTablePdf, downloadCsv, type TableExport } from '@/lib/tableExport';

/** The school's financial year runs with the calendar, so the year of the
 *  date is the whole of it. Kept local until lib/financialYear lands. */
const fyForDate = (iso: string): string => `FY${String(iso).slice(0, 4)}`;

const TYPE_LABELS: Record<AccountType, string> = {
  asset: 'Asset', liability: 'Liability', equity: 'Equity',
  income: 'Income', expense: 'Expenditure',
};

const TYPE_TONE: Record<AccountType, string> = {
  asset: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200',
  liability: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  equity: 'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200',
  income: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  expense: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200',
};

interface Draft {
  code: string; name: string; type: AccountType; parentCode: string;
  isPostable: boolean; active: boolean; sortOrder: number;
  /** Set only when editing — a code is never editable in place. */
  editingExisting: boolean;
}

const emptyDraft = (): Draft => ({
  code: '', name: '', type: 'expense', parentCode: '',
  isPostable: true, active: true, sortOrder: 0, editingExisting: false,
});

export default function ChartOfAccounts() {
  // Same convention as Vouchers.tsx: the screen resolves its own financial
  // year rather than having one threaded down from App.
  const fy = fyForDate(new Date().toISOString());
  const { userRoles } = useAuth();
  const perms = getPermissions(userRoles);
  const { accounts, loading, error } = useGlData(fy);

  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [showInactive, setShowInactive] = useState(false);

  const mayEdit = perms.canManageChartOfAccounts;

  /** Headers with their children beneath, in sortOrder. Orphans (a parentCode
   *  pointing nowhere) are surfaced at the end rather than silently dropped. */
  const tree = useMemo(() => {
    const visible = accounts.filter((a) => showInactive || a.active);
    const headers = visible.filter((a) => !a.parentCode).sort((a, b) => a.sortOrder - b.sortOrder);
    const childrenOf = (code: string) =>
      visible.filter((a) => a.parentCode === code).sort((a, b) => a.sortOrder - b.sortOrder);
    const placed = new Set<string>();
    const groups = headers.map((h) => {
      const kids = childrenOf(h.code);
      placed.add(h.code);
      kids.forEach((k) => placed.add(k.code));
      return { header: h, children: kids };
    });
    const orphans = visible.filter((a) => !placed.has(a.code));
    return { groups, orphans };
  }, [accounts, showInactive]);

  const openNew = () => { setMsg(null); setDraft(emptyDraft()); };
  const openEdit = (a: GlAccount) => {
    setMsg(null);
    setDraft({
      code: a.code, name: a.name, type: a.type, parentCode: a.parentCode ?? '',
      isPostable: a.isPostable, active: a.active, sortOrder: a.sortOrder,
      editingExisting: true,
    });
  };

  async function save() {
    if (!draft) return;
    const code = draft.code.trim();
    if (!code) { setMsg({ ok: false, text: 'A code is required — it is the account id.' }); return; }
    if (!draft.name.trim()) { setMsg({ ok: false, text: 'A name is required.' }); return; }
    if (!draft.editingExisting && accounts.some((a) => a.code === code)) {
      setMsg({ ok: false, text: `Code ${code} already exists. Codes are unique — edit that account instead.` });
      return;
    }
    setSaving(true);
    try {
      await saveAccount(
        {
          code,
          name: draft.name.trim(),
          type: draft.type,
          parentCode: draft.parentCode.trim() || undefined,
          isPostable: draft.isPostable,
          active: draft.active,
          sortOrder: Number(draft.sortOrder) || 0,
        },
        'client',
      );
      setMsg({ ok: true, text: `Saved ${code} — ${draft.name.trim()}.` });
      setDraft(null);
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(a: GlAccount) {
    if (a.systemKey && a.active) {
      setMsg({
        ok: false,
        text: `${a.code} carries the system key ${a.systemKey} — posting resolves it by that key, so deactivating it would break future postings. Move the key to another account first.`,
      });
      return;
    }
    try {
      await setAccountActive(a.code, !a.active, 'client');
      setMsg({ ok: true, text: `${a.code} ${a.active ? 'deactivated' : 'reactivated'}.` });
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    }
  }

  const exportDoc = useMemo((): TableExport<GlAccount> => ({
    title: 'Chart of Accounts',
    meta: `${accounts.filter((a) => a.active).length} active  ·  ${accounts.filter((a) => a.isPostable && a.active).length} postable`,
    filename: 'chart-of-accounts',
    columns: [
      { header: 'Code', width: 18, text: (a) => a.code },
      { header: 'Account', text: (a) => a.name },
      { header: 'Type', width: 24, text: (a) => a.type },
      { header: 'Postable', width: 22, text: (a) => (a.isPostable ? 'Yes' : 'Header') },
      { header: 'System key', width: 40, text: (a) => a.systemKey ?? '' },
      { header: 'Active', width: 18, text: (a) => (a.active ? 'Yes' : 'No') },
    ],
    sections: [
      ...tree.groups.map((g) => ({ heading: `${g.header.code}  ${g.header.name}`, rows: g.children })),
      ...(tree.orphans.length ? [{ heading: 'Not under a header', rows: tree.orphans }] : []),
    ],
    note: 'Header accounts group the chart and cannot be posted to. A system key is how the '
      + 'software finds an account — those are wired to posting rules and should not be reused.',
  }), [tree, accounts]);

  if (loading) return <Spinner label="Loading chart of accounts..." />;

  const row = (a: GlAccount, isHeader: boolean) => (
    <div
      key={a.code}
      className={`flex items-center gap-3 px-3 py-2 ${isHeader ? 'bg-gray-50 dark:bg-gray-800/60 font-semibold' : ''} ${a.active ? '' : 'opacity-50'}`}
    >
      <span className={`font-mono text-sm ${isHeader ? '' : 'pl-6'} text-gray-500 dark:text-gray-400 w-24 shrink-0`}>{a.code}</span>
      <span className="flex-1 text-sm text-gray-800 dark:text-gray-100">{a.name}</span>
      {a.systemKey && (
        <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-primary/10 text-primary" title="Posting engine resolves this account by this key — do not deactivate">
          {a.systemKey}
        </span>
      )}
      {!a.isPostable && (
        <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300" title="Header — subtotals only, cannot take journal lines">
          header
        </span>
      )}
      <span className={`text-[10px] px-1.5 py-0.5 rounded ${TYPE_TONE[a.type]}`}>{TYPE_LABELS[a.type]}</span>
      {mayEdit && (
        <span className="flex items-center gap-1">
          <button type="button" onClick={() => openEdit(a)} title="Edit" className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700">
            <Pencil size={14} />
          </button>
          <button
            type="button"
            onClick={() => toggleActive(a)}
            title={a.active ? 'Deactivate (history is kept)' : 'Reactivate'}
            className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
          >
            {a.active ? <EyeOff size={14} /> : <Eye size={14} />}
          </button>
        </span>
      )}
    </div>
  );

  // The chart itself, grouped as it is displayed. This is the first thing an
  // accountant asks for when taking a new system on, and the first thing they
  // want to mark up on paper.
  return (
    <div className="space-y-4">
      <PageHeader icon={ListTree}
        title="Chart of Accounts"
        subtitle={`${accounts.filter((a) => a.active).length} active accounts · ${accounts.filter((a) => a.isPostable && a.active).length} postable`}
        actions={(
          <>
            <ExportBar onPdf={() => downloadTablePdf(exportDoc)} onCsv={() => downloadCsv(exportDoc)} disabled={!accounts.length} />
            {mayEdit && <Button icon={<Plus size={16} />} onClick={openNew}>New account</Button>}
          </>
        )}
      />

      {error && (
        <SoftCard pad="p-3">
          <p className="text-sm text-rose-600 flex items-center gap-2"><AlertTriangle size={15} />{error}</p>
        </SoftCard>
      )}

      {msg && (
        <SoftCard pad="p-3">
          <p className={`text-sm flex items-start gap-2 ${msg.ok ? 'text-emerald-700 dark:text-emerald-300' : 'text-rose-600'}`}>
            {msg.ok ? <Check size={15} className="mt-0.5 shrink-0" /> : <AlertTriangle size={15} className="mt-0.5 shrink-0" />}
            {msg.text}
          </p>
        </SoftCard>
      )}

      <SoftCard pad="p-3">
        <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
          <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
          Show deactivated accounts
        </label>
      </SoftCard>

      {accounts.length === 0 ? (
        <SoftCard pad="p-6">
          <p className="text-sm text-gray-500 text-center">
            No accounts yet. Seed the starter chart with <code className="font-mono">node seed/seed-gl-accounts.cjs --commit</code>.
          </p>
        </SoftCard>
      ) : (
        <SoftCard pad="p-0">
          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            {tree.groups.map((g) => (
              <div key={g.header.code}>
                {row(g.header, true)}
                {g.children.map((c) => row(c, false))}
              </div>
            ))}
            {tree.orphans.length > 0 && (
              <div>
                <div className="px-3 py-2 bg-amber-50 dark:bg-amber-900/20 text-sm font-semibold text-amber-800 dark:text-amber-200">
                  Ungrouped — parent code does not exist
                </div>
                {tree.orphans.map((a) => row(a, false))}
              </div>
            )}
          </div>
        </SoftCard>
      )}

      {draft && (
        <FormModal
          title={draft.editingExisting ? `Edit account ${draft.code}` : 'New account'}
          onClose={() => setDraft(null)}
          footer={
            <>
              <button type="button" className={modalGhostBtn} onClick={() => setDraft(null)}>Cancel</button>
              <button type="button" className={modalPrimaryBtn} disabled={saving} onClick={save}>
                {saving ? 'Saving…' : 'Save account'}
              </button>
            </>
          }
        >
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">Code</label>
                <input
                  className={glassFieldClass('mt-1 font-mono')}
                  value={draft.code}
                  disabled={draft.editingExisting}
                  onChange={(e) => setDraft({ ...draft, code: e.target.value })}
                  placeholder="5610"
                />
                {draft.editingExisting && (
                  <p className="text-[11px] text-gray-500 mt-1">
                    The code is the account id, so it cannot be changed here — create the new code and deactivate this one.
                  </p>
                )}
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">Sort order</label>
                <input
                  type="number"
                  className={glassFieldClass('mt-1')}
                  value={draft.sortOrder}
                  onChange={(e) => setDraft({ ...draft, sortOrder: Number(e.target.value) })}
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">Name</label>
              <input
                className={glassFieldClass('mt-1')}
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                placeholder="Security"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">Type</label>
                <select
                  className={glassFieldClass('mt-1')}
                  value={draft.type}
                  onChange={(e) => setDraft({ ...draft, type: e.target.value as AccountType })}
                >
                  {(Object.keys(TYPE_LABELS) as AccountType[]).map((t) => (
                    <option key={t} value={t}>{TYPE_LABELS[t]}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">Group under (parent code)</label>
                <select
                  className={glassFieldClass('mt-1')}
                  value={draft.parentCode}
                  onChange={(e) => setDraft({ ...draft, parentCode: e.target.value })}
                >
                  <option value="">— top level (a header) —</option>
                  {accounts.filter((a) => !a.isPostable).map((a) => (
                    <option key={a.code} value={a.code}>{a.code} — {a.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={draft.isPostable}
                onChange={(e) => setDraft({ ...draft, isPostable: e.target.checked })}
              />
              Postable — journal lines may be coded to this account
            </label>
            <p className="text-[11px] text-gray-500 -mt-1">
              Leave unticked for a heading that only subtotals its children.
            </p>

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={draft.active}
                onChange={(e) => setDraft({ ...draft, active: e.target.checked })}
              />
              Active
            </label>
          </div>
        </FormModal>
      )}
    </div>
  );
}
